import { Buffer } from "buffer";
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    IconButton,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ArrowBack } from "@mui/icons-material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";

import globalStyles from "../../globalStyles";
import IconMenu from "../atoms/IconMenu";
import Profile from "../molecules/Profile";
import { AppDispatch, RootState } from "../../configureStore";
import { SET_HAS_CHANGES } from "../../redux/reducer/returns";
import { useTheme } from "../../ThemeContext";

interface ReturnsAppParams {}

const ReturnsApp = ({}: ReturnsAppParams) => {
    const { instance, accounts } = useMsal();
    const request = { scopes: [`api://${import.meta.env.VITE_TOKEN_AUDIENCE}/api`] };
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();
    const key = searchParams.get("key");
    const { mode, toggleColorMode } = useTheme();
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns?.hasChanges || false);
    const location = useLocation();
    const navigate = useNavigate();
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const params = useParams();
    const loanId = useMemo<string | undefined>(() => params.loanId, [params]);

    // Only show back button on sub-routes, not on main /returns page
    const showBackButton = location.pathname !== "/returns";

    const markUserLeaving = useCallback(async (id: string) => {
        try {
            const [fiCode, ...ln] = id.split("_");
            const loanNumber = ln.join("_");
            // You'll need to create this API endpoint
            await fetch(`${import.meta.env.VITE_ENDPOINT}/returns/mark-exit`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${sessionStorage.getItem("ACCESS_TOKEN")}`,
                    "OCP-APIM-Subscription-Key": import.meta.env.VITE_OCP_APIM_SUB_KEY ?? "",
                },
                body: JSON.stringify({
                    loanId,
                    loanNumber,
                    fiCode,
                    key,
                }),
            });
        } catch (error) {
            console.error("Failed to mark user exit:", error);
        }
    }, []);

    const handleBackClick = useCallback(() => {
        if (hasChanges) {
            setShowConfirmDialog(true);
        } else {
            if (loanId) markUserLeaving(loanId);
            navigate("/returns");
        }
    }, [hasChanges, navigate]);

    const handleConfirmLeave = useCallback(() => {
        // Clear changes and navigate
        dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
        setShowConfirmDialog(false);
        navigate("/returns");
    }, [dispatch, navigate]);

    const handleCancelLeave = useCallback(() => {
        setShowConfirmDialog(false);
    }, []);

    useEffect(() => {
        const getToken = () => {
            instance
                .acquireTokenSilent({
                    ...request,
                    account: accounts[0],
                })
                .then(({ accessToken, idToken }) => {
                    sessionStorage.setItem("ACCESS_TOKEN", accessToken);
                    sessionStorage.setItem("ID_TOKEN", idToken);
                    window.dispatchEvent(new Event("token"));

                    try {
                        sessionStorage.setItem(
                            "ACCESS_EXPIRY",
                            (
                                JSON.parse(Buffer.from(accessToken.split(".")[1], "base64").toString("ascii")).exp
                                * 1000
                            ).toString(),
                        );
                    } catch (err) {
                        console.error(err);
                    }
                })
                .catch((err) => {
                    console.error(err);
                });
        };
        getToken();
        const intervalId = setInterval(getToken, 300000);

        return () => clearInterval(intervalId);
    }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <Box sx={[globalStyles.flexCenterBetween, globalStyles.pa1]}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                        component="img"
                        sx={{
                            height: 40,
                            // width: 170,
                        }}
                        alt="Halcyon Still Water"
                        src={mode === "dark" ? "/logo-white.png" : "/logo.png"}
                    />
                    {showBackButton && (
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={handleBackClick}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                    borderColor: "primary.dark",
                                    backgroundColor: "primary.light",
                                },
                            }}
                        >
                            Back to Returns
                        </Button>
                    )}
                </Box>
                <Box>
                    <IconButton
                        sx={{ ml: 1 }}
                        onClick={toggleColorMode}
                        color="inherit"
                    >
                        {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                    </IconButton>
                    <IconMenu
                        name="AccountCircleOutlined"
                        item={<Profile user={accounts[0]} />}
                    />
                </Box>
            </Box>
            <Box
                component="main"
                sx={[
                    {
                        flex: 1,
                        paddingBottom: 1,
                        minHeight: 0,
                        overflow: "auto",
                    },
                    globalStyles.px1,
                    globalStyles.pt1,
                ]}
            >
                <Outlet />
            </Box>

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={handleCancelLeave}
            >
                <DialogTitle>Unsaved Changes</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelLeave}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmLeave}
                        color="error"
                        variant="contained"
                    >
                        Leave Without Saving
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ReturnsApp;
